'use client';

/**
 * Program Billing Activities Configuration
 *
 * Allows configuring billable activities for a program:
 * - Add/edit/delete billing activities
 * - Set rates per activity
 * - Configure payer-specific rates
 * - Import from Integration Workbench
 * - Define bundled activities
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  DollarSign,
  FileText,
  Upload,
  Download,
  Link2,
  Settings2,
  Stethoscope,
  MapPin,
  Check
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BillingActivityDependencies } from '@/components/admin/BillingActivityDependencies';

interface BillingActivity {
  id?: number;
  program_id: number;
  activity_name: string;
  activity_code?: string;
  activity_type?: string;
  description?: string;
  cpt_code?: string;
  hcpcs_code?: string;
  revenue_code?: string;
  billing_unit: string;
  rate_per_unit: number;
  max_units_per_day?: number;
  max_units_per_month?: number;
  place_of_service?: string;
  requires_prior_auth: boolean;
  is_active: boolean;
  effective_date?: string;
  expiration_date?: string;
}

interface DiagnosisCode {
  id: number;
  code: string;
  description: string;
  short_description?: string;
  category?: string;
  is_active: boolean;
}

interface ActivityDiagnosisLink {
  id: number;
  billing_activity_id: number;
  icd10_code_id: number;
  icd10_code: string;
  icd10_description: string;
  is_default: boolean;
  display_order: number;
}

interface PlaceOfServiceMaster {
  code: string;
  label: string;
  description?: string;
  is_active: boolean;
}

interface ProgramPlaceOfService {
  id: number;
  program_id: number;
  place_of_service_code: string;
  place_of_service_label: string;
  is_default: boolean;
  display_order: number;
  is_active: boolean;
}

type MaxUnitsPeriod = 'month' | 'year' | 'quarter' | 'week' | 'day' | 'episode';

interface ProgramBillingActivitiesProps {
  programId: number;
  programName?: string;
  maxUnitsPeriod?: MaxUnitsPeriod;
  onUpdate?: () => void;
}

// Helper function to get period label
const getPeriodLabel = (period: MaxUnitsPeriod = 'month'): string => {
  const labels: Record<MaxUnitsPeriod, string> = {
    month: 'Month',
    year: 'Year',
    quarter: 'Quarter',
    week: 'Week',
    day: 'Day',
    episode: 'Episode',
  };
  return labels[period] || 'Month';
};

// Helper function to get period short label
const getPeriodShortLabel = (period: MaxUnitsPeriod = 'month'): string => {
  const labels: Record<MaxUnitsPeriod, string> = {
    month: 'Month',
    year: 'Year',
    quarter: 'Qtr',
    week: 'Week',
    day: 'Day',
    episode: 'Ep',
  };
  return labels[period] || 'Month';
};

export function ProgramBillingActivities({
  programId,
  programName,
  maxUnitsPeriod = 'month',
  onUpdate
}: ProgramBillingActivitiesProps) {
  const { t } = useAppTranslation();
  const [activities, setActivities] = useState<BillingActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState<BillingActivity | null>(null);

  const [formData, setFormData] = useState<BillingActivity>({
    program_id: programId,
    activity_name: '',
    activity_type: 'care_coordination',
    billing_unit: 'per_month',
    rate_per_unit: 0,
    requires_prior_auth: false,
    is_active: true
  });
  const [activeTab, setActiveTab] = useState('activities');

  // Diagnosis codes state
  const [diagnosisCodes, setDiagnosisCodes] = useState<DiagnosisCode[]>([]);
  const [diagnosisCategories, setDiagnosisCategories] = useState<string[]>([]);
  const [activityDiagnosisLinks, setActivityDiagnosisLinks] = useState<Record<number, ActivityDiagnosisLink[]>>({});
  const [selectedActivityForDiagnosis, setSelectedActivityForDiagnosis] = useState<number | null>(null);
  const [selectedDiagnosisCodes, setSelectedDiagnosisCodes] = useState<number[]>([]);
  const [diagnosisFilterCategory, setDiagnosisFilterCategory] = useState<string>('');

  // Place of Service state
  const [masterPosCodes, setMasterPosCodes] = useState<PlaceOfServiceMaster[]>([]);
  const [programPosCodes, setProgramPosCodes] = useState<ProgramPlaceOfService[]>([]);
  const [selectedPosCodes, setSelectedPosCodes] = useState<string[]>([]);
  const [defaultPosCode, setDefaultPosCode] = useState<string>('');

  const activityTypes = [
    { value: 'assessment', label: 'Assessment' },
    { value: 'care_planning', label: 'Care Planning' },
    { value: 'care_coordination', label: 'Care Coordination' },
    { value: 'case_management', label: 'Case Management' },
    { value: 'counseling', label: 'Counseling' },
    { value: 'doula_services', label: 'Doula Services' },
    { value: 'education', label: 'Education' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'housing_support', label: 'Housing Support' },
    { value: 'employment_support', label: 'Employment Support' },
    { value: 'other', label: 'Other' }
  ];

  const billingUnits = [
    { value: 'per_month', label: 'Per Month' },
    { value: 'per_session', label: 'Per Session' },
    { value: 'per_15min', label: 'Per 15 Minutes' },
    { value: 'per_30min', label: 'Per 30 Minutes' },
    { value: 'per_hour', label: 'Per Hour' },
    { value: 'per_day', label: 'Per Day' },
    { value: 'per_visit', label: 'Per Visit' },
    { value: 'per_encounter', label: 'Per Encounter' }
  ];

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(cboId && { 'X-CBO-ID': cboId })
    };
  };

  useEffect(() => {
    fetchActivities();
    fetchDiagnosisCodes();
    fetchDiagnosisCategories();
    fetchMasterPosCodes();
    fetchProgramPosCodes();
  }, [programId]);

  // Fetch diagnosis codes from master table
  const fetchDiagnosisCodes = async () => {
    try {
      const response = await fetch(
        getWeaveinApiUrl('/api/diagnosis-codes'),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setDiagnosisCodes(data);
      }
    } catch (err) {
      console.error('Error fetching diagnosis codes:', err);
    }
  };

  // Fetch diagnosis categories
  const fetchDiagnosisCategories = async () => {
    try {
      const response = await fetch(
        getWeaveinApiUrl('/api/diagnosis-codes/categories'),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setDiagnosisCategories(data);
      }
    } catch (err) {
      console.error('Error fetching diagnosis categories:', err);
    }
  };

  // Fetch linked diagnosis codes for an activity
  const fetchActivityDiagnosisCodes = async (activityId: number) => {
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/billing-activities/${activityId}/diagnosis-codes`),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setActivityDiagnosisLinks(prev => ({ ...prev, [activityId]: data }));
        // Set selected codes for the form
        setSelectedDiagnosisCodes(data.map((link: ActivityDiagnosisLink) => link.icd10_code_id));
      }
    } catch (err) {
      console.error('Error fetching activity diagnosis codes:', err);
    }
  };

  // Save activity diagnosis code links
  const saveActivityDiagnosisCodes = async () => {
    if (!selectedActivityForDiagnosis) return;

    setLoading(true);
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/billing-activities/${selectedActivityForDiagnosis}/diagnosis-codes/bulk`),
        {
          method: 'PUT',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: JSON.stringify({ icd10_code_ids: selectedDiagnosisCodes })
        }
      );
      if (response.ok) {
        await fetchActivityDiagnosisCodes(selectedActivityForDiagnosis);
        setError(null);
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (array of {type, loc, msg, input, url})
        if (Array.isArray(errorData.detail)) {
          const messages = errorData.detail.map((e: any) => e.msg || JSON.stringify(e)).join('; ');
          setError(messages || 'Failed to save diagnosis codes');
        } else if (typeof errorData.detail === 'object') {
          setError(JSON.stringify(errorData.detail));
        } else {
          setError(errorData.detail || 'Failed to save diagnosis codes');
        }
      }
    } catch (err: any) {
      setError(typeof err.message === 'string' ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Fetch master place of service codes
  const fetchMasterPosCodes = async () => {
    try {
      const response = await fetch(
        getWeaveinApiUrl('/api/place-of-service/master'),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setMasterPosCodes(data);
      }
    } catch (err) {
      console.error('Error fetching master POS codes:', err);
    }
  };

  // Fetch program-specific place of service codes
  const fetchProgramPosCodes = async () => {
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/programs/${programId}/place-of-service`),
        { credentials: 'include', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setProgramPosCodes(data);
        setSelectedPosCodes(data.map((pos: ProgramPlaceOfService) => pos.place_of_service_code));
        const defaultPos = data.find((pos: ProgramPlaceOfService) => pos.is_default);
        if (defaultPos) {
          setDefaultPosCode(defaultPos.place_of_service_code);
        }
      }
    } catch (err) {
      console.error('Error fetching program POS codes:', err);
    }
  };

  // Save program place of service configuration
  const saveProgramPosCodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/programs/${programId}/place-of-service/bulk`),
        {
          method: 'PUT',
          credentials: 'include',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            codes: selectedPosCodes,
            default_code: defaultPosCode
          })
        }
      );
      if (response.ok) {
        await fetchProgramPosCodes();
        setError(null);
      } else {
        const errorData = await response.json();
        // Handle Pydantic validation errors (array of {type, loc, msg, input, url})
        if (Array.isArray(errorData.detail)) {
          const messages = errorData.detail.map((e: any) => e.msg || JSON.stringify(e)).join('; ');
          setError(messages || 'Failed to save place of service configuration');
        } else if (typeof errorData.detail === 'object') {
          setError(JSON.stringify(errorData.detail));
        } else {
          setError(errorData.detail || 'Failed to save place of service configuration');
        }
      }
    } catch (err: any) {
      setError(typeof err.message === 'string' ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/programs/${programId}/billing-activities`),
        { credentials: 'include', headers: getAuthHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        setActivities(data);
      } else {
        throw new Error('Failed to fetch billing activities');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const url = editingActivity
        ? `/api/billing-activities/${editingActivity.id}`
        : '/api/billing-activities';

      const method = editingActivity ? 'PUT' : 'POST';

      const response = await fetch(getWeaveinApiUrl(url), {
        method,
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchActivities();
        resetForm();
        if (onUpdate) onUpdate();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save activity');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (activityId: number) => {
    if (!window.confirm('Are you sure you want to delete this billing activity?')) {
      return;
    }

    try {
      const response = await fetch(
        getWeaveinApiUrl(`/api/billing-activities/${activityId}`),
        {
          method: 'DELETE',
          credentials: 'include',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        await fetchActivities();
        if (onUpdate) onUpdate();
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (activity: BillingActivity) => {
    setEditingActivity(activity);
    setFormData(activity);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      program_id: programId,
      activity_name: '',
      activity_type: 'care_coordination',
      billing_unit: 'per_month',
      rate_per_unit: 0,
      requires_prior_auth: false,
      is_active: true
    });
    setEditingActivity(null);
    setShowForm(false);
  };

  const handleImportFromWorkbench = () => {
    // TODO: Implement Integration Workbench import
    alert('Integration Workbench import coming soon!');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            {t('billing.billing_activities', 'Billing Activities')}
          </h2>
          {programName && (
            <p className="text-sm text-gray-600 mt-1">
              Configure billable activities for {programName}
            </p>
          )}
        </div>
        {activeTab === 'activities' && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleImportFromWorkbench}>
              <Upload className="h-4 w-4 mr-2" />
              {t('billing.import_from_workbench', 'Import from Workbench')}
            </Button>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('billing.add_activity', 'Add Activity')}
            </Button>
          </div>
        )}
      </div>

      {error && (
        <Card className="p-4 bg-red-50 border-red-200 text-red-800">
          {error}
        </Card>
      )}

      {/* Tabs for Activities, Diagnosis Codes, POS, and Dependencies */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="activities" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            {t('billing.activities', 'Activities')}
          </TabsTrigger>
          <TabsTrigger value="diagnosis" className="flex items-center gap-2">
            <Stethoscope className="h-4 w-4" />
            {t('billing.diagnosis_codes', 'Diagnosis Codes')}
          </TabsTrigger>
          <TabsTrigger value="pos" className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {t('billing.place_of_service', 'Place of Service')}
          </TabsTrigger>
          <TabsTrigger value="dependencies" className="flex items-center gap-2">
            <Link2 className="h-4 w-4" />
            {t('billing.dependencies', 'Dependencies')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activities" className="mt-6">

      {/* Activity Form */}
      {showForm && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {editingActivity ? 'Edit' : 'Add'} Billing Activity
            </h3>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label htmlFor="activity_name">{t('billing.activity_name', 'Activity Name *')}</Label>
              <Input
                id="activity_name"
                value={formData.activity_name}
                onChange={(e) => setFormData({ ...formData, activity_name: e.target.value })}
                placeholder="e.g., Monthly Care Coordination"
                required
              />
            </div>

            <div>
              <Label htmlFor="activity_type">{t('billing.activity_type', 'Activity Type')}</Label>
              <select
                id="activity_type"
                className="w-full p-2 border rounded"
                value={formData.activity_type}
                onChange={(e) => setFormData({ ...formData, activity_type: e.target.value })}
              >
                {activityTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="activity_code">{t('billing.activity_code', 'Activity Code')}</Label>
              <Input
                id="activity_code"
                value={formData.activity_code || ''}
                onChange={(e) => setFormData({ ...formData, activity_code: e.target.value })}
                placeholder={t('billing.internal_code', 'Internal code')}
              />
            </div>

            <div>
              <Label htmlFor="cpt_code">{t('billing.cpt_code', 'CPT Code')}</Label>
              <Input
                id="cpt_code"
                value={formData.cpt_code || ''}
                onChange={(e) => setFormData({ ...formData, cpt_code: e.target.value })}
                placeholder="e.g., 99490"
              />
            </div>

            <div>
              <Label htmlFor="hcpcs_code">{t('billing.hcpcs_code', 'HCPCS Code')}</Label>
              <Input
                id="hcpcs_code"
                value={formData.hcpcs_code || ''}
                onChange={(e) => setFormData({ ...formData, hcpcs_code: e.target.value })}
                placeholder="e.g., G0506"
              />
            </div>

            <div>
              <Label htmlFor="billing_unit">{t('billing.billing_unit', 'Billing Unit *')}</Label>
              <select
                id="billing_unit"
                className="w-full p-2 border rounded"
                value={formData.billing_unit}
                onChange={(e) => setFormData({ ...formData, billing_unit: e.target.value })}
                required
              >
                {billingUnits.map(unit => (
                  <option key={unit.value} value={unit.value}>{unit.label}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="rate_per_unit">{t('billing.rate_per_unit', 'Rate Per Unit ($) *')}</Label>
              <Input
                id="rate_per_unit"
                type="number"
                step="0.01"
                min="0"
                value={formData.rate_per_unit}
                onChange={(e) => setFormData({ ...formData, rate_per_unit: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>

            <div>
              <Label htmlFor="max_units_per_month">
                {t('billing.max_units_per_period', `Max Units Per ${getPeriodLabel(maxUnitsPeriod)}`)}
              </Label>
              <Input
                id="max_units_per_month"
                type="number"
                min="0"
                value={formData.max_units_per_month || ''}
                onChange={(e) => setFormData({ ...formData, max_units_per_month: parseInt(e.target.value) || undefined })}
                placeholder={t('billing.optional', 'Optional')}
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="description">{t('billing.description', 'Description')}</Label>
              <Input
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('billing.brief_description_of_this_activity', 'Brief description of this activity')}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requires_prior_auth"
                checked={formData.requires_prior_auth}
                onChange={(e) => setFormData({ ...formData, requires_prior_auth: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="requires_prior_auth" className="cursor-pointer">
                {t('billing.requires_prior_authorization', 'Requires Prior Authorization')}
              </Label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4"
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                {t('billing.active', 'Active')}
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={resetForm}>
              {t('billing.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : 'Save Activity'}
            </Button>
          </div>
        </Card>
      )}

      {/* Activities List */}
      <div className="space-y-3">
        {loading && activities.length === 0 ? (
          <Card className="p-8 text-center text-gray-500">
            {t('billing.loading_billing_activities', 'Loading billing activities...')}
          </Card>
        ) : activities.length === 0 ? (
          <Card className="p-8 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('billing.no_billing_activities', 'No Billing Activities')}</h3>
            <p className="text-gray-600 mb-4">
              {t('billing.add_billing_activities_to_configure', 'Add billing activities to configure what services can be billed for this program.')}
            </p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('billing.add_first_activity', 'Add First Activity')}
            </Button>
          </Card>
        ) : (
          activities.map(activity => (
            <Card key={activity.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{activity.activity_name}</h3>
                    {!activity.is_active && (
                      <Badge className="bg-gray-100 text-gray-800">{t('billing.inactive', 'Inactive')}</Badge>
                    )}
                    {activity.requires_prior_auth && (
                      <Badge className="bg-yellow-100 text-yellow-800">{t('billing.prior_auth_required', 'Prior Auth Required')}</Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {activity.cpt_code && (
                      <div>
                        <span className="text-gray-500">CPT:</span>
                        <span className="ml-2 font-medium">{activity.cpt_code}</span>
                      </div>
                    )}
                    {activity.hcpcs_code && (
                      <div>
                        <span className="text-gray-500">HCPCS:</span>
                        <span className="ml-2 font-medium">{activity.hcpcs_code}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">{t('billing.rate', 'Rate:')}</span>
                      <span className="ml-2 font-medium text-green-600">
                        ${activity.rate_per_unit} {activity.billing_unit.replace('_', ' ')}
                      </span>
                    </div>
                    {activity.max_units_per_month && (
                      <div>
                        <span className="text-gray-500">
                          {t('billing.max_per_period', `Max/${getPeriodShortLabel(maxUnitsPeriod)}:`)}
                        </span>
                        <span className="ml-2 font-medium">{activity.max_units_per_month}</span>
                      </div>
                    )}
                  </div>

                  {activity.description && (
                    <p className="text-sm text-gray-600 mt-2">{activity.description}</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(activity)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => activity.id && handleDelete(activity.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
        </TabsContent>

        {/* Diagnosis Codes Tab */}
        <TabsContent value="diagnosis" className="mt-6">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Link ICD-10 Diagnosis Codes to Activities</h3>
                <p className="text-sm text-gray-600">
                  Select an activity and assign the diagnosis codes that can be used when billing for that activity.
                  These codes will appear in the diagnosis dropdown during claims entry.
                </p>
              </div>

              {/* Activity Selector */}
              <div>
                <Label htmlFor="diagnosis_activity">Select Activity</Label>
                <select
                  id="diagnosis_activity"
                  className="w-full p-2 border rounded mt-1"
                  value={selectedActivityForDiagnosis || ''}
                  onChange={(e) => {
                    const activityId = parseInt(e.target.value);
                    setSelectedActivityForDiagnosis(activityId || null);
                    if (activityId) {
                      fetchActivityDiagnosisCodes(activityId);
                    } else {
                      setSelectedDiagnosisCodes([]);
                    }
                  }}
                >
                  <option value="">-- Select an activity --</option>
                  {activities.filter(a => a.is_active).map(activity => (
                    <option key={activity.id} value={activity.id}>
                      {activity.activity_name} {activity.cpt_code ? `(${activity.cpt_code})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {selectedActivityForDiagnosis && (
                <>
                  {/* Category Filter */}
                  <div>
                    <Label htmlFor="diagnosis_category_filter">Filter by Category</Label>
                    <select
                      id="diagnosis_category_filter"
                      className="w-full p-2 border rounded mt-1"
                      value={diagnosisFilterCategory}
                      onChange={(e) => setDiagnosisFilterCategory(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {diagnosisCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  {/* Diagnosis Codes Selection */}
                  <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
                    <div className="space-y-2">
                      {diagnosisCodes
                        .filter(dx => !diagnosisFilterCategory || dx.category === diagnosisFilterCategory)
                        .map(dx => (
                          <label
                            key={dx.id}
                            className={`flex items-start gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                              selectedDiagnosisCodes.includes(dx.id) ? 'bg-blue-50 border border-blue-200' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedDiagnosisCodes.includes(dx.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDiagnosisCodes(prev => [...prev, dx.id]);
                                } else {
                                  setSelectedDiagnosisCodes(prev => prev.filter(id => id !== dx.id));
                                }
                              }}
                              className="mt-1 h-4 w-4"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-medium text-blue-600">{dx.code}</span>
                                {dx.category && (
                                  <Badge className="text-xs bg-gray-100 text-gray-700">{dx.category}</Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-700">{dx.description}</p>
                              {dx.short_description && (
                                <p className="text-xs text-gray-500">{dx.short_description}</p>
                              )}
                            </div>
                          </label>
                        ))}
                      {diagnosisCodes.length === 0 && (
                        <p className="text-center text-gray-500 py-4">
                          No diagnosis codes available. Run the database migration to seed ICD-10 codes.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Selected Count & Save */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-sm text-gray-600">
                      {selectedDiagnosisCodes.length} diagnosis code(s) selected
                    </span>
                    <Button onClick={saveActivityDiagnosisCodes} disabled={loading}>
                      <Save className="h-4 w-4 mr-2" />
                      {loading ? 'Saving...' : 'Save Diagnosis Codes'}
                    </Button>
                  </div>
                </>
              )}

              {!selectedActivityForDiagnosis && activities.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Stethoscope className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>Add billing activities first, then you can link diagnosis codes to them.</p>
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Place of Service Tab */}
        <TabsContent value="pos" className="mt-6">
          <Card className="p-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Configure Place of Service Codes</h3>
                <p className="text-sm text-gray-600">
                  Select which Place of Service codes are valid for this program. Only selected codes will appear
                  in the claims entry dropdown. Set a default code that will be auto-selected.
                </p>
              </div>

              {/* Default POS Selection */}
              <div>
                <Label htmlFor="default_pos">Default Place of Service</Label>
                <select
                  id="default_pos"
                  className="w-full p-2 border rounded mt-1"
                  value={defaultPosCode}
                  onChange={(e) => setDefaultPosCode(e.target.value)}
                >
                  <option value="">-- No default --</option>
                  {selectedPosCodes.map(code => {
                    const pos = masterPosCodes.find(p => p.code === code);
                    return (
                      <option key={code} value={code}>
                        {code} - {pos?.label || code}
                      </option>
                    );
                  })}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  The default will be auto-selected when creating claims for this program.
                </p>
              </div>

              {/* POS Codes Selection */}
              <div>
                <Label>Available Place of Service Codes</Label>
                <div className="border rounded-lg p-4 max-h-96 overflow-y-auto mt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {masterPosCodes.map(pos => (
                      <label
                        key={pos.code}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${
                          selectedPosCodes.includes(pos.code) ? 'bg-green-50 border border-green-200' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPosCodes.includes(pos.code)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPosCodes(prev => [...prev, pos.code]);
                            } else {
                              setSelectedPosCodes(prev => prev.filter(c => c !== pos.code));
                              if (defaultPosCode === pos.code) {
                                setDefaultPosCode('');
                              }
                            }
                          }}
                          className="h-4 w-4"
                        />
                        <div className="flex-1">
                          <span className="font-mono font-medium">{pos.code}</span>
                          <span className="ml-2 text-sm text-gray-700">{pos.label}</span>
                          {defaultPosCode === pos.code && (
                            <Badge className="ml-2 bg-green-100 text-green-800 text-xs">Default</Badge>
                          )}
                        </div>
                      </label>
                    ))}
                    {masterPosCodes.length === 0 && (
                      <p className="text-center text-gray-500 py-4 col-span-2">
                        No place of service codes available. Run the database migration to seed POS codes.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Selected Count & Save */}
              <div className="flex items-center justify-between pt-4 border-t">
                <span className="text-sm text-gray-600">
                  {selectedPosCodes.length} place of service code(s) selected
                  {defaultPosCode && ` (Default: ${defaultPosCode})`}
                </span>
                <Button onClick={saveProgramPosCodes} disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Configuration'}
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="dependencies" className="mt-6">
          <BillingActivityDependencies
            programId={programId}
            activities={activities.map(a => ({
              id: a.id!,
              activity_name: a.activity_name,
              activity_code: a.activity_code
            }))}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ProgramBillingActivities;
